import {
  Badge,
  Button,
  Card,
  createSortableColumn,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { Eye, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import type { LeaveRequestDTO } from "@/modules/hr/domain/contracts";
import { formatDateLocal } from "@/shared/lib/date-time";

const STATUS_LABELS: Record<string, string> = {
  pending_line: "ລໍຖ້າຫົວໜ້າສາຍ",
  pending_hr: "ລໍຖ້າ HR",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

const STATUS_VARIANTS: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  approved: "default",
  pending_line: "secondary",
  pending_hr: "outline",
  rejected: "destructive",
};

type LeaveRequestTableProps = {
  data: LeaveRequestDTO[];
  isLoading?: boolean;
  totalCount: number;
  offset: number;
  limit: number;
  onPageChange?: (offset: number, limit: number) => void;
  statusFilter?: string;
  onStatusFilterChange?: (status: string | undefined) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange?: (id: string, desc: boolean) => void;
  /** Called when user clicks Edit (only shown when status !== "approved") */
  onEdit?: (id: string) => void;
  /** Called when user confirms Delete (only shown when status !== "approved") */
  onDelete?: (id: string) => void;
};

export function LeaveRequestTable({
  data,
  isLoading,
  totalCount,
  offset,
  limit,
  onPageChange,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
}: LeaveRequestTableProps) {
  const navigate = useNavigate({ from: "/app/hr/leave/requests" });

  const columns: TanstackReactTable.ColumnDef<LeaveRequestDTO>[] = [
    createSortableColumn<LeaveRequestDTO>("leaveTypeName", "ປະເພດ", {
      size: 140,
      cell: ({ row }) => row.original.leaveTypeName,
    }),
    createSortableColumn<LeaveRequestDTO>("startDate", "ວັນທີ", {
      size: 180,
      cell: ({ row }) =>
        `${formatDateLocal(row.original.startDate)} – ${formatDateLocal(row.original.endDate)}`,
    }),
    createSortableColumn<LeaveRequestDTO>("days", "ຈຳນວນວັນ", {
      size: 120,
      cell: ({ row }) => `${row.original.days} ວັນ`,
    }),
    createSortableColumn<LeaveRequestDTO>("status", "ສະຖານະ", {
      size: 140,
      cell: ({ row }) => (
        <Badge variant={STATUS_VARIANTS[row.original.status] ?? "outline"}>
          {STATUS_LABELS[row.original.status] ?? row.original.status}
        </Badge>
      ),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const item = row.original;
        const canEditDelete = item.status === "pending_line";
        const hasEdit = canEditDelete && !!onEdit;
        const hasDelete = canEditDelete && !!onDelete;
        const actionCount = 1 + (hasEdit ? 1 : 0) + (hasDelete ? 1 : 0);

        if (actionCount === 1) {
          return (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={() =>
                navigate({
                  to: "/app/hr/leave/requests/$id",
                  params: { id: item.id },
                })
              }
              aria-label="ເບິ່ງລາຍລະອຽດ"
            >
              <Eye className="size-4" />
            </Button>
          );
        }

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label="ຈັດການ"
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={4}>
              <DropdownMenuItem
                onClick={() =>
                  navigate({
                    to: "/app/hr/leave/requests/$id",
                    params: { id: item.id },
                  })
                }
              >
                <Eye className="mr-2 size-4" />
                ເບິ່ງລາຍລະອຽດ
              </DropdownMenuItem>
              {hasEdit && (
                <DropdownMenuItem onClick={() => onEdit?.(item.id)}>
                  <Pencil className="mr-2 size-4" />
                  ແກ້ໄຂ
                </DropdownMenuItem>
              )}
              {hasDelete && (
                <DropdownMenuItem
                  className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                  onClick={() => onDelete?.(item.id)}
                >
                  <Trash2 className="mr-2 size-4" />
                  ລຶບ
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      size: 130,
    },
  ];

  return (
    <Card className="gap-0">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 pb-4">
        <h3 className="font-semibold">ປະຫວັດການຂໍລາ</h3>
        {onStatusFilterChange && (
          <div className="flex gap-1">
            {(
              [
                "all",
                "pending_line",
                "pending_hr",
                "approved",
                "rejected",
              ] as const
            ).map((s) => (
              <Button
                key={s}
                type="button"
                variant={
                  (s === "all" && !statusFilter) || statusFilter === s
                    ? "default"
                    : "secondary"
                }
                size="sm"
                className={
                  (s === "all" && !statusFilter) || statusFilter === s
                    ? "bg-black text-white hover:bg-black/90"
                    : ""
                }
                onClick={() =>
                  onStatusFilterChange(s === "all" ? undefined : s)
                }
              >
                {s === "all" ? "ທັງໝົດ" : (STATUS_LABELS[s] ?? s)}
              </Button>
            ))}
          </div>
        )}
      </div>
      <DataTable<LeaveRequestDTO, unknown>
        noDataMessage="ຍັງບໍ່ມີຄຳຂໍລາ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={
          onPageChange ? (p) => onPageChange(p.offset, p.limit) : undefined
        }
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (!onSortingChange) return;
          if (!sorting || sorting.length === 0) return;
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id ?? "", !!sorting[0]?.desc);
        }}
      />
    </Card>
  );
}
